import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { snowUrl } from 'service/config';
import { memo, useCallback, useState, useMemo, useEffect } from 'react';
import DetailedAlerts from '../AlertDetails/DetailedAlerts';
import DoneIcon from '@mui/icons-material/Done';
import InputBase from '@mui/material/InputBase';
import { ReactComponent as EditIcon } from 'assets/PepWise2.0/edit-img.svg';
import { ReactComponent as PlayIcon } from 'assets/PepWise2.0/play-outline-blue.svg';
import { ReactComponent as DocumentBlueIcon } from 'assets/PepWise2.0/document-blue.svg';
import { ReactComponent as DocumentIcon } from 'assets/PepWise2.0/document-grey.svg';
import Tooltip from '@mui/material/Tooltip';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { createComments, createIncidentComments, getSopMetrics } from 'service/unifiedAlerts';
import CircularProgress from '@mui/material/CircularProgress';
import { FilterOptionsOps } from '../hooks/useUnifiedIncidents';
import { GroupedTableFormatter } from 'components/Common/GroupedTableFormatter';
import GroupByShowMore from 'components/Common/GroupByShowMore';
import ChecklistIcon from '@mui/icons-material/Checklist';
import Drawer from '@mui/material/Drawer';
import TasksRecords from './TasksRecords';
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { PlayCircleFilledSharp } from '@mui/icons-material';
import KbArticlesCell, { KbArticle } from './KbArticlesCell';
import { useColumnFilters } from 'components/ColumnFilters/useColumnFilters';
import type { FilterOperator, SortModel } from 'components/ColumnFilters/types';
import {
  ColumnFilterPayloadItem,
  columnFilterPayloadToModel,
  mergeColumnFilterPayload,
  toColumnFiltersPayload,
  withColumnFilterOperators,
} from '../columnFilterUtils';
import { useNavigate } from 'react-router-dom';

const OPS_FREE_TEXT_COLUMNS = ['number', 'short_description', 'close_notes', 'close_code', 'category', 'subcategory', 'opened_by'];
const TEXT_SEARCH_OPERATORS: FilterOperator[] = ['startswith', 'contains', 'notcontains'];

export interface Incident {
  number: string;
  priority: string;
  category: string;
  subcategory: string;
  cmdb_ci: string;
  short_description: string;
  description: string;
  resolved_date: string;
  opened_at: string;
  closed_at: string;
  assignment_group: string;
  channel: string;
  impact?: string;
  urgency?: string;
  close_code?: string;
  close_notes?: string;
  billable?: boolean;
  sys_updated_on: string;
  sys_created_on: string;
  sys_id: string;
  comments: string;
  incident_state: string;
  state?: string;
  sector?: string | string[];
  domain?: string;
  program?: string;
  u_source?: string;
  sopId?: string;
  sopName?: string;
  sopLink?: string;
  kbArticles?: KbArticle[];
  area?: string;
  u_vendor_name?: string;
  parent: string;
  isDandAIAG: boolean;
  vendor: string;
}

// Define an explicit interface for your drawer state
interface DrawerState {
  bottom: boolean;
  parent: string | null; // Allows string or null to match your function parameters
  state: string | undefined;
  age: string | null;
  sysId: string;
}

interface IncidentsTableProps {
  incidents: Incident[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  filterOptions: FilterOptionsOps;
  onPageSizeChange: (pageSize: number) => void;
  onSorting: (sortInfo: { sortBy: string; sortDir: string }) => void;
  sortingInfo: { sortBy: string; sortDir: string };
  isLoading: boolean;
  parentTile: string;
  onGroupByChange?: (groupBy: string) => void;
  groupByList?: { label: string; value: string }[];
  groupBy?: string;
  includeGroupByMenuColumns?: string[];
  onGroupToggle?: (row: any) => void;
  onShowMoreClick?: (row: any) => void;
  loadingGroupId?: string | null;
  isDataOpsAdmin?: boolean;
  onManageSop?: () => void;
  columnFilters?: ColumnFilterPayloadItem[];
  onColumnFilterChange?: (payload: ColumnFilterPayloadItem[], sortModel: SortModel) => void;
  getColumnFilterValues?: (field: string) => Promise<any[]>;
  liveTile?: string;
}

const PriorityChip = ({ row }: { row: Incident }) => {

  const colorMap = [
    { key: '1', bg: '#37b66e', color: '#fff' }, // Lowest
    { key: '2', bg: '#facc32', color: '#fff' },
    { key: '3', bg: '#F04438', color: '#fff' },
    { key: '4', bg: '#F79009', color: '#fff' },
    { key: '5', bg: '#EAAA08', color: '#fff' }, // Highest
  ];

  // Find the mapping where the priority string (e.g., "3 - Moderate") contains the key
  const match = colorMap.find(item => row.priority?.includes(item.key));

  const style = match
    ? { bg: match.bg, color: match.color }
    : { bg: '#E0E0E0', color: '#000' };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '75px',
        justifyContent: 'center',
      }}
    >
      {row.priority}
    </Box>
  );
};

const renderPriorityFormatter = ({ row }: { row: Incident }) => (row as any)?.isParent || (row as any)?.isShowMoreRow ? null : row.priority ? <PriorityChip row={row} /> : 'NA';

const formatAgeDuration = (diffMs: number): string => {
  if (Number.isNaN(diffMs) || diffMs < 0) return '-';

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const formatOpenAge = (createdOn: any): string =>
  formatAgeDuration(Date.now() - (createdOn ? new Date(createdOn).getTime() : NaN));


const AGE_NUMBER_OPERATORS: FilterOperator[] = ['equals', 'gt', 'lt', 'between'];
const DAY_MS = 24 * 60 * 60 * 1000;
const AGE_TIMESTAMP_MIN = 1e10;


const toAgeDayFilter = (filter: ColumnFilterPayloadItem): ColumnFilterPayloadItem => {
  const now = Date.now();
  const [first, second] = filter.values.map(Number);
  const oldestOfDay = (days: number) => now - (days + 1) * DAY_MS + 1;
  const newestOfDay = (days: number) => now - days * DAY_MS;

  if (filter.operator === 'equals') return { ...filter, operator: 'between', values: [oldestOfDay(first), newestOfDay(first)] };
  if (filter.operator === 'gt') return { ...filter, operator: 'lte', values: [now - (first + 1) * DAY_MS] };
  if (filter.operator === 'lt') return { ...filter, operator: 'gt', values: [newestOfDay(first)] };
  if (filter.operator === 'between') {
    const [from, to] = [Math.min(first, second), Math.max(first, second)];
    return { ...filter, values: [oldestOfDay(to), newestOfDay(from)] };
  }
  return filter;
};

const formatUpdatedTime = (value: any): string => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value ?? '') : d.toLocaleString('en-US');
};

const renderTimeFormatter = ({ row }: { row: Incident }) => {
  if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <span> 
        {formatUpdatedTime(row.sys_updated_on)} 
      </span>

      <CalendarMonthOutlinedIcon
        sx={{
          fontSize: 15,
          color: '#98A2B3',
        }}
      />
    </Box>
  ); 
};

const renderAgeFormatter = ({ row }: { row: Incident }) => {
  if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;

  const isClosed =
    row?.incident_state === 'Closed' ||
    row?.incident_state === 'Canceled';

  const startTime = isClosed
    ? (row?.opened_at ? new Date(row.opened_at).getTime() : NaN)
    : (row?.sys_created_on ? new Date(row.sys_created_on).getTime() : NaN);

  const endTime = isClosed
    ? (row?.closed_at ? new Date(row.closed_at).getTime() : NaN)
    : Date.now();

  return formatAgeDuration(endTime - startTime);
};

const SNOW_ROUTE_MAP: Record<string, string> = {
  "Problem Tickets": "problem",
  "Incident Tickets": "incident",
  "Change Requests": "change_request",
  "Service Requests": "sc_req_item",
};

export const buildReverseSectorMap = (
  sectorMap: Record<string, string[] | string> = {}
): Record<string, string> => {
  const reverseMap: Record<string, string> = {};

  Object.entries(sectorMap).forEach(([parent, values]) => {
    if (Array.isArray(values)) {
      values.forEach((val) => {
        reverseMap[val] = parent;
      });
    } else {
      reverseMap[values] = parent;
      reverseMap[parent] = parent;
    }
  });

  return reverseMap;
};


const isEmptyValue = (value: any) => {
  if (value === null || value === undefined) return true;

  if (typeof value === "string") return value.trim() === "";

  if (Array.isArray(value)) return value.length === 0;

  return false;
};

const getRowsForColumnCheck = (rows: Incident[]) => {
  return rows.flatMap((row) => {
    const groupedRow = row as any;

    if (groupedRow?.isShowMoreRow) return [];

    if (groupedRow?.isParent) {
      return Array.isArray(groupedRow.children) ? groupedRow.children : [];
    }

    if (Array.isArray(groupedRow?.children)) {
      return groupedRow.children;
    }

    return [row];
  });
};

const hasAnyValue = (rows: any[], key: string) => {
  return rows.some((row) => !isEmptyValue(row?.[key]));
};

const autoColumnWidth = (min = 140, flex = 1, max = min * 1.5) =>
  `minmax(clamp(${min}px, ${min / 10}vw, ${max}px), ${flex}fr)`;

export const mapDomainValue = (val: string) => {
  if (!val) return null;

  if (val === 'Other' || val === 'Others') return 'Others';

  if (
    val === 'Global' ||
    val === 'Global/Supply Chain' ||
    val === 'Supply Chain'
  ) {
    return 'Global';
  }

  if (
    val === 'Consumer' ||
    val === 'Consumer & Suppy Chain' ||
    val === 'Consumer & Supply Chain'
  ) {
    return 'Consumer';
  }

  return val;
};

export const getSnowRecordUrl = (parentTile: any, sys_id: string, snowUrl: string) => {
    const type = SNOW_ROUTE_MAP[parentTile as any] ?? "incident";
    return `${snowUrl}/now/sow/record/${type}/${sys_id}`;
  };

const IncidentsTable = memo(({
  incidents,
  total,
  page,
  pageSize,
  onPageChange,
  filterOptions,
  onPageSizeChange,
  onSorting,
  sortingInfo,
  isLoading,
  parentTile,
  onGroupByChange,
  groupByList = [],
  groupBy = 'none',
  includeGroupByMenuColumns = [],
  onGroupToggle,
  onShowMoreClick,
  loadingGroupId,
  isDataOpsAdmin,
  onManageSop,
  columnFilters = [],
  onColumnFilterChange,
  getColumnFilterValues,
  liveTile,
}: IncidentsTableProps) => {

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = theme.palette;
  const [isAlertsDrawerOpen, setIsAlertsDrawerOpen] = useState(false);
  const [entireRow, setEntireRow] = useState<Incident | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [commentErrors, setCommentErrors] = useState<Record<string, string>>({});
  const [statusMap, setStatusMap] = useState<
    Record<string, 'idle' | 'loading' | 'success' | 'error'>
  >({});
  const [userPendingCount, setUserPendingCount] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;
    const fetchUserPendingCount = async () => {
      try {
        const res = await getSopMetrics('OPS_PULSE');
        const count = res?.data?.userPendingCount ?? res?.userPendingCount ?? 0;
        if (isMounted && typeof count === 'number') {
          setUserPendingCount(count);
        }
      } catch (err) {
        console.error('Error fetching user pending count in IncidentsTable:', err);
      }
    };

    if (onManageSop && isDataOpsAdmin) {
      fetchUserPendingCount();
    }

    const interval = setInterval(() => {
      if (onManageSop && isDataOpsAdmin) {
        fetchUserPendingCount();
      }
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [onManageSop, isDataOpsAdmin]);

    // 1. Initialized with correct type safety rules
  const [openBottomDrawer, setOpenBottomDrawer] = useState<DrawerState>({ 
    bottom: false, 
    parent: null,
    state: undefined, 
    age: null,
    sysId: ''
  });

   const toggleDrawer =
    (open: boolean, state: string | undefined,  parent: string | null, age: string | null, sysId: string) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      // 3. Fixed state updates to accept null values safely
      setOpenBottomDrawer(prev => ({
        ...prev,
        bottom: open,
        parent: parent, 
        state: state,
        age: age,
        sysId: sysId
      }));
    };

  const taskMap: Record<string, string> = {
  'Service Requests': 'SC-Task',
  'Problem Tickets': 'P-Task',
  'Change Requests': 'C-Task',
};

  const tooltipTitle = taskMap[parentTile]
  ? `Show ${taskMap[parentTile]}`
  : parentTile;

  const renderAlertIDFormatter = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    return (
      <Typography
        variant='tableRowFont'
        sx={{
          color: `${colors.table.moreIcon} !important`,
          cursor: "pointer",
          textDecoration: "underline",
          display: "flex",
          alignItems: "center",
          justifyContent: 'space-between',
          gap: 0.5,
        }}
        onClick={() =>
          window.open(
            getSnowRecordUrl(parentTile, row.sys_id, snowUrl),
            "_blank"
          )
        }
      >
        {row.number}
        {
          parentTile !== 'Incidents' && (
             <Box
                sx={{ cursor: "pointer", display: "flex" }}
                onClick={(e) => {
                  e.stopPropagation();
                  const age = renderAgeFormatter({row}) 
                  const sysId = row?.sys_id
                  toggleDrawer(true, row.state, row.number, age, sysId)(e);
                }}
              >
                <Tooltip
                      title={tooltipTitle}
                      followCursor={true}
                      arrow
                      placement={"top-start"}
                    >
                      <ChecklistIcon sx={{ color: `${colors.table.moreIcon} !important` }} />
                </Tooltip>
              </Box>
          )
        }
       
      </Typography>
    );
  };

  const validateComment = (incidentId: string, value: string) => {
    if (value.length > 500) {
      setCommentErrors((prev) => ({
        ...prev,
        [incidentId]: `Maximum ${500} characters allowed`,
      }));
      return false;
    }

    setCommentErrors((prev) => {
      const next = { ...prev };
      delete next[incidentId];
      return next;
    });

    return true;
  };


  const handleAlertsClick = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const handleViewDetailsClick = (row: Incident) => {
      setEntireRow(row);
      setIsAlertsDrawerOpen(true);
    };
    return (
      <Typography
        variant='tableRowFont'
        sx={{
          color: "dodgerblue !important",
          cursor: "pointer",
        }}
        onClick={() => handleViewDetailsClick(row)}
      >
        VIEW DETAILS
      </Typography>
    );
  };

  const renderKbArticlesFormatter = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    return <KbArticlesCell articles={row.kbArticles ?? []} />;
  };


  const handleCommentChange = (incidentId: string, value: string) => {
    setComments((prev) => ({
      ...prev,
      [incidentId]: value,
    }));

    validateComment(incidentId, value);
  };

  const handleCommentSubmit = async (row: Incident) => {
    const comment = comments[row.number] ?? '';

    // 🚫 block submission if invalid
    if (!validateComment(row.number, comment)) {
      return;
    }
    const payload = {
      incidentId: row.number,
      comments: comment,
    };

    // 👉 set loading
    setStatusMap((prev) => ({
      ...prev,
      [row.number]: 'loading',
    }));

    try {
      const resp = await createIncidentComments(payload);

      if (resp === 'Comments Added Successfully') {
        setStatusMap((prev) => ({
          ...prev,
          [row.number]: 'success',
        }));
      } else {
        throw new Error('Failed');
      }
    } catch (e) {
      console.info(JSON.stringify(e))
      setStatusMap((prev) => ({
        ...prev,
        [row.number]: 'error',
      }));
    }

    setTimeout(() => {
      setStatusMap((prev) => ({
        ...prev,
        [row.number]: 'idle',
      }));
    }, 2000);

    setEditingRow(null);
  };

  const renderCommentFormatter = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const value = comments[row.number] ?? row.comments ?? '';
    const isEditing = editingRow === row.number;
    const status = statusMap[row.number] || 'idle';
    const error = commentErrors[row.number];

    let actionElement: React.ReactNode;


    if (error) {
      actionElement = (
        <IconButton
          size="small"
          disabled={!error}
          sx={{ p: 0.5 }}
        >
          <Tooltip title={error} arrow>
            <ErrorOutlineIcon
              fontSize='small'
              sx={{
                color: '#ef4444',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </IconButton>
      )
    }
    else if (!error && status === 'loading') {
      actionElement = <CircularProgress size={16} />;
    } else if (!error && status === 'success') {
      actionElement = <Box sx={{ fontSize: '14px' }}>✅</Box>;
    } else if (!error && status === 'error') {
      actionElement = <Box sx={{ fontSize: '14px' }}>❌</Box>;
    } else if (!error && isEditing) {
      actionElement = (
        <IconButton
          size="small"
          disabled={!!error}
          onMouseDown={(e) => {
            e.preventDefault(); // important to prevent blur
            handleCommentSubmit(row);
          }}
          sx={{ p: 0.5, color: colors.backgroundLight.icon }}
        >
          <DoneIcon fontSize="small" />
        </IconButton>
      );
    } else {
      actionElement = (
        <IconButton
          size="small"
          onClick={() => setEditingRow(row.number)}
          sx={{ p: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      );
    }

    return (
      <Box
        sx={{
          width: 220,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
        onClick={() => !isEditing && setEditingRow(row.number)}
      >
        <InputBase
          value={value}
          readOnly={!isEditing}
          onChange={(e) =>
            handleCommentChange(row.number, e.target.value)
          }
          onBlur={() => {
            if (isEditing) {
              setEditingRow(null);
            }
          }}
          placeholder="Add Comment..."
          sx={{
            flex: 1,
            fontSize: '0.85rem',
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: isEditing ? '#f5f7fb' : 'transparent',
            border: error ? '1px solid #ef4444' : '1px solid transparent', // 🔴 highlight
            '& .MuiInputBase-input': {
              color: '#000',
              padding: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#6b7280',
              opacity: 1,
            },
          }}
        />
        {actionElement}
      </Box>
    );
  };

  const renderButtons = ({ row }: { row: Incident }) => {

    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const isSopLinked = row?.sopId;
    const playActionElement = (
      <Tooltip title="Self Heal">
        <IconButton size="small" sx={{ p: 0.5, color: colors.buttons.primary.disabledBg }}>
          <PlayCircleFilledSharp fontSize="small" />
        </IconButton>
      </Tooltip>
    );

    const descriptionActionElement = (
      <Tooltip title={isSopLinked ? 'SOP Linked' : 'SOP Not Linked'} arrow>
        <IconButton
          size="small"
          sx={{
            p: 0.5,
            cursor: isSopLinked ? 'pointer' : 'default',
            '& svg': {
              width: 18,
              height: 18,
            },
          }}
          onClick={() => {
            if (row.sopLink) {
              window.open(row.sopLink, '_blank');
            }
          }}
        >
          {isSopLinked ? (
            <DocumentBlueIcon />
          ) : (
            <DocumentIcon />
          )}
        </IconButton>
      </Tooltip>
    );

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {playActionElement}
        {descriptionActionElement}
      </Box>
    );
  };

  const getMappedSector = (value: any, reverseMap: any) => {
    if (Array.isArray(value)) {
      return value.map(v => reverseMap[v] || v).join(", ");
    }

    return reverseMap[value] || value;
  };

  const reverseSectorMap = useMemo(() => {
    return buildReverseSectorMap(
      filterOptions?.sectorDropdownValues ?? {}
    );
  }, [filterOptions]);

  const groupByMenuColumnKeys = useMemo(() => {
    const keys = [
      ...includeGroupByMenuColumns,
      ...groupByList.map((item) => item.value),
    ];

    return Array.from(new Set(keys.filter(Boolean)));
  }, [includeGroupByMenuColumns, groupByList]);

  const getGroupedCellDisplayValue = useCallback((row: any, groupByValue: string) => {
    const value = row?.[groupByValue] ?? row?.parentGroupValue ?? row?.groupValue ?? row?.key ?? row?.value ?? row?.name ?? 'Unassigned';

    if (groupByValue === 'sector') {
      return getMappedSector(value, reverseSectorMap) || 'Unassigned';
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return value || 'Unassigned';
  }, [reverseSectorMap]);


  const renderSectorFormatter = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const mapedSector = getMappedSector(row.sector, reverseSectorMap)
    return (
      <Typography
        variant='tableRowFont'
      >
        {mapedSector}
      </Typography>
    )
  };

  const renderAreaFormatter = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    return (
      <Typography
        variant='tableRowFont'
      >
        {row?.isDandAIAG === true ? 'YES' : 'No'}
      </Typography>
    )
  };

  const renderDomainFormatter = ({ row }: { row: Incident }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;

    const mappedDomain = mapDomainValue(row.domain!);

    return (
      <Typography variant="tableRowFont">
        {mappedDomain}
      </Typography>
    );
  };

  const statusKey = parentTile === 'Incidents' ? 'incident_state' : 'state';
  const numberLabel = parentTile === "Problem Tickets" ? 'PROBLEM ID' : parentTile === 'Service Requests' ? 'SERVICE REQUEST ID' : parentTile === 'Change Requests' ? 'CHANGE REQUEST ID' : 'INCIDENT ID';

  const getCountLabel = useCallback(() => {
    if (parentTile === 'Problem Tickets') return 'Problems';
    if (parentTile === 'Service Requests') return 'Service Requests';
    if (parentTile === 'Change Requests') return 'Change Requests';
    return 'Incidents';
  }, [parentTile]);

  const getColumnsWithGrouping = useCallback((baseColumns: any[], groupByValue: string) => {
    if (!groupByValue || groupByValue === 'none') {
      return baseColumns;
    }

    const groupedColumnIndex = baseColumns.findIndex(col => col.key === groupByValue);
    const groupedColumn = groupedColumnIndex !== -1 ? baseColumns[groupedColumnIndex] : null;

    const groupedFirstColumn = {
      key: groupByValue,
      name: groupedColumn ? groupedColumn.name : groupByValue.toUpperCase().replace(/_/g, ' '),
      minWidth: 250,  // Minimum width
      width: autoColumnWidth(250, 2),
      formatter: ({ row }: any) => {
        if (row.isShowMoreRow) {
          const groupValue = row.parentRow?.[groupByValue] ?? row?.[groupByValue] ?? row?.parentRow?.groupValue ?? row?.parentGroupValue;
          const isGroupLoading = loadingGroupId === `${groupByValue}-${groupValue}`;
          return (
            <GroupByShowMore
              row={row}
              handleShowMoreClick={onShowMoreClick || (() => undefined)}
              isLoading={isGroupLoading}
            />
          );
        }

        return (
          <GroupedTableFormatter
            row={row}
            groupByColumn={groupByValue}
            groupByLabel={getGroupedCellDisplayValue(row, groupByValue)}
            onToggle={onGroupToggle || (() => undefined)}
            countLabel={getCountLabel()}
          />
        );
      },
      resizable: true,
      flexGrow: 2,
      sortable: true
    };

    let remainingColumns = [...baseColumns];
    if (groupedColumnIndex !== -1) {
      remainingColumns = baseColumns.filter((_, index) => index !== groupedColumnIndex);
    }

    remainingColumns = remainingColumns.map((column: any) => {
      const originalFormatter = column.formatter;
      const originalRenderCell = column.renderCell;

      return {
        ...column,
        renderCell: undefined,
        formatter: (props: any) => {
          const row = props?.row;

          if (row?.isParent || row?.isShowMoreRow) {
            return null;
          }

          if (originalFormatter) {
            return originalFormatter(props);
          }

          if (originalRenderCell) {
            return originalRenderCell(props);
          }

          const value = row?.[column.key];
          return Array.isArray(value) ? value.join(', ') : value ?? '';
        }
      };
    });

    const newColumns = [groupedFirstColumn, ...remainingColumns];

    return newColumns;
  }, [getCountLabel, getGroupedCellDisplayValue, loadingGroupId, onGroupToggle, onShowMoreClick]);

  // const defaultColumns = parentTile === 'Problem Tickets' ? ['PROBLEM ID', 'TITLE', 'PRIORITY', 'STATUS', 'AGE', 'ASSIGNMENT GROUP', 'LAST UPDATED', 'COMMENTS', 'ACTIVITY', 'ACTIONS'] :
  //   parentTile === 'Service Requests' ? ['SERVICE REQUEST ID', 'TITLE', 'PRIORITY', 'STATUS', 'AGE', 'ASSIGNMENT GROUP', 'LAST UPDATED', 'COMMENTS', 'ACTIVITY', 'ACTIONS'] :
  //     parentTile === 'Change Requests' ? ['CHANGE REQUEST ID', 'TITLE', 'PRIORITY', 'STATUS', 'AGE', 'ASSIGNMENT GROUP', 'LAST UPDATED', 'COMMENTS', 'ACTIVITY', 'ACTIONS'] :
  //       ['INCIDENT ID', 'TITLE', 'PRIORITY', 'STATUS', 'AGE', 'ASSIGNMENT GROUP', 'LAST UPDATED', 'COMMENTS', 'ACTIVITY', 'ACTIONS'];

  const defaultColumns = [numberLabel, 'TITLE', 'PRIORITY', 'STATUS', 'AGE', 'D&AI', 'VENDOR' ,'ASSIGNMENT GROUP', 'LAST UPDATED', 'SECTOR', 'DOMAIN', 'DEPARTMENT', 'PROGRAM', 'COMMENTS', 'KB ARTICLES', 'ACTIVITY', 'ACTIONS', 'SLA Resolution Miss', 'SLA Response Miss'];

  const columns = useMemo(() => {
    const allColumns = [
      { key: 'number', name: numberLabel, width: autoColumnWidth(240, 1), formatter: renderAlertIDFormatter },
      {
        key: 'short_description', name: 'TITLE', width: autoColumnWidth(260, 2), formatter: ({ row }: { row: Incident }) => {
          const short_description = row.short_description ?? '';
          return (
            <span title={short_description}>
              {short_description.length > 50 ? `${short_description.slice(0, 50)}...` : short_description}
            </span>
          );
        }
      },
      {
        key: 'priority',
        name: 'PRIORITY',
        width: autoColumnWidth(150, 1),
        renderCell: PriorityChip,
        formatter: renderPriorityFormatter,
      },
      { key: statusKey, name: 'STATUS', width: autoColumnWidth(150, 1) },
      {
        key: 'age',
        name: 'AGE',
        width: autoColumnWidth(120),
        formatter: renderAgeFormatter,
        formatFilterValue: formatOpenAge,
        showValueList: liveTile !== 'Closed Incidents',
      },
      {
        key: 'isDandAIAG',
        name: 'D&AI',
        width: autoColumnWidth(120),
        formatter: renderAreaFormatter,
        isFilterOptionDisabled: (optionValue: string, selectedValues: string[]) =>
          selectedValues.length > 0 && !selectedValues.includes(optionValue),
      },
      { key: 'vendor', name: 'VENDOR', width: autoColumnWidth(120)},
      { key: 'assignment_group', name: 'ASSIGNMENT GROUP', width: autoColumnWidth(180, 1) },
      {
        key: 'sys_updated_on',
        name: 'LAST UPDATED',
        width: autoColumnWidth(180, 1),
        formatter: renderTimeFormatter,
        formatFilterValue: formatUpdatedTime,
      },
      { key: 'sector', name: 'SECTOR', width: autoColumnWidth(140, 1), formatter: renderSectorFormatter },
      { key: 'domain', name: 'DOMAIN', width: autoColumnWidth(140, 1), formatter: renderDomainFormatter },
      { key: 'department', name: 'DEPARTMENT', width: autoColumnWidth(140, 1) },
      { key: 'program', name: 'PROGRAM', width: autoColumnWidth(140, 1) },
      {
        key: 'comments',
        name: 'COMMENTS',
        width: autoColumnWidth(250, 1),
        formatter: renderCommentFormatter,
      },
      {
        key: 'kbArticles',
        name: 'KB ARTICLES',
        width: autoColumnWidth(110, 1),
        formatter: renderKbArticlesFormatter,
      },
      {
        key: 'activity',
        name: 'ACTIVITY',
        width: autoColumnWidth(80),
        formatter: renderButtons,
      },
      { key: 'actions', name: 'ACTIONS', width: autoColumnWidth(150, 1), formatter: handleAlertsClick },
      { key: 'category', name: 'CATEGORY', width: autoColumnWidth(140, 1) },
      { key: 'subcategory', name: 'SUB CATEGORY', width: autoColumnWidth(140, 1) },
      { key: 'cmdb_ci', name: 'CI', width: autoColumnWidth(120, 1) },
      { key: 'contact_type', name: 'CHANNEL', width: autoColumnWidth(120, 1) },      
      { key: 'capability', name: 'Capability', width: autoColumnWidth(140, 1) },
      { key: 'severity', name: 'Severity', width: autoColumnWidth(140, 1) },      
      { key: 'businessUnit', name: 'Business Unit', width: autoColumnWidth(150, 1) },
      { key: 'service_offering', name: 'Service Offering', width: autoColumnWidth(160, 1) },
      { key: 'made_sla', name: 'Made SLA', width: autoColumnWidth(130, 1) },
      { key: 'resolution_slaHasBreached', name: 'SLA Resolution Miss', width: autoColumnWidth(130, 1) },
      { key: 'response_slaHasBreached', name: 'SLA Response Miss', width: autoColumnWidth(130, 1) },
      { key: 'close_code', name: 'Closed Code', width: autoColumnWidth(150, 1) },
      { key: 'close_notes', name: 'Close Notes', width: autoColumnWidth(150, 1) },
      { key: 'problem_state', name: 'Problem State', width: autoColumnWidth(150, 1) },
      { key: 'known_error', name: 'Known Error', width: autoColumnWidth(140, 1) },
      { key: 'u_source', name: 'Source', width: autoColumnWidth(130, 1) },
      { key: 'type', name: 'Type', width: autoColumnWidth(120, 1) },
      { key: 'opened_at', name: 'OPEN DATE', width: autoColumnWidth(160, 1) },
      { key: 'opened_by', name: 'OPENED FOR', width: autoColumnWidth(150, 1) },
      { key: 'impact', name: 'IMPACT', width: autoColumnWidth(120, 1) },
      { key: 'urgency', name: 'URGENCY', width: autoColumnWidth(120, 1) },
    ];

    const rowsForColumnCheck = getRowsForColumnCheck(incidents);

    const alwaysShow = ['number', 'short_description', 'priority', statusKey, 'age', 'cmdb_ci', 'assignment_group', 'service_offering', 'kbArticles', 'activity', 'actions', 'comments', 'sector', 'domain', 'department', 'program', 'isDandAIAG'];

    const baseColumns = allColumns.filter((col) => {
      if (alwaysShow.includes(col.key)) return true;

      if (groupBy && groupBy !== 'none' && col.key === groupBy) return true;

      return hasAnyValue(rowsForColumnCheck, col.key);
    });

    if (groupBy && groupBy !== 'none') {
      return getColumnsWithGrouping(baseColumns, groupBy);
    }

    return baseColumns;

  }, [incidents, numberLabel, statusKey, comments, editingRow, statusMap, commentErrors, groupBy, getColumnsWithGrouping, liveTile]);

  const filterConfiguredColumns = useMemo(
    () => withColumnFilterOperators(columns, OPS_FREE_TEXT_COLUMNS)
      .map((column) => (column.key === 'age' ? { ...column, columnDetails: { age: 'number' }, showValueList: false, filterOperators: AGE_NUMBER_OPERATORS } : column))
      .map((column) => (column.key === 'short_description' ? { ...column, showValueList: false, filterOperators: TEXT_SEARCH_OPERATORS } : column))
      .map((column) => (column.key === 'number' || column.key === 'sys_updated_on' || column.key === 'close_notes' || column.key === 'opened_at' ? { ...column, showValueList: false } : column))
      .map((column) => (column.key === 'close_notes' ? { ...column, filterOperators: TEXT_SEARCH_OPERATORS } : column)),
    [columns]
  );

  const getColumnFilterOptions = useCallback(
    async (field: string): Promise<any[]> => (await getColumnFilterValues?.(field)) ?? [],
    [getColumnFilterValues]
  );

  const columnFilterController = useColumnFilters({
    columns: filterConfiguredColumns,
    rows: incidents,
    mode: 'server',
    initialFilterModel: columnFilterPayloadToModel(columnFilters),
    onChange: ({ filterModel, sortModel, changedColumnKey }) => {
      const payload = mergeColumnFilterPayload(columnFilters, toColumnFiltersPayload(filterModel), changedColumnKey);
      const expandedPayload = payload.map((filter) => {
       
        if (filter.id === 'age' && filter.values.length > 0 && filter.values.every((value) => Number(value) < AGE_TIMESTAMP_MIN)) {
          return toAgeDayFilter(filter);
        }
        return filter;
      });
      onColumnFilterChange?.(expandedPayload, sortModel);
    },
  });
  const isColumnFilterEnabled = Boolean(onColumnFilterChange);

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        '& .rdg': {
          width: '100% !important',
          minWidth: '100% !important',
        },
        '& .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
          paddingTop: '16px !important',
          paddingBottom: '16px !important',
        },
        '& .rdg-header-row .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
        },
        '& .rdg-row': {
          borderBlockEnd: '1px solid #fff !important',
        },
      }}
    >
      <CustomizedDataGrid
        key="snow-unified-incidents-grid"
        toolbarSx={{ mb: '10px' }}
        tableColumns={columns}
        tableData={incidents}
        tableType={`UnifiedIncidentsSnow_${parentTile}`}
        defaultColumnNames={defaultColumns}
        listLength={total}
        page={page}
        pageSize={pageSize}
        paginationData={{ totalElements: total, totalPages: Math.ceil(total / pageSize) }}
        onSorting={onSorting}
        currSorting={sortingInfo}
        handlePageChange={(_: any, p: number) => onPageChange(p)}
        handlepageSizeChange={(e: any) => onPageSizeChange(Number.parseInt(e.target.value))}
        isFetching={isLoading}
        isDateRangeFilter={true}
        isDisabled={false}
        isShowMoreButton={true}
        pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
        isPrevNextPagination
        isRenderPagination={total > 0}
        isGroupBy={true}
        onGroupByChange={onGroupByChange}
        groupBy={groupBy}
        groupByList={groupByList}
        includeGroupByMenuColumns={includeGroupByMenuColumns}
        isSortIcon={true}
        enableColumnFilter={isColumnFilterEnabled}
        columnFilterController={isColumnFilterEnabled ? columnFilterController : undefined}
        getColumnFilterValues={getColumnFilterValues ? getColumnFilterOptions : undefined}
      >
        {onManageSop && isDataOpsAdmin && (
          <Box
            component="button"
            onClick={onManageSop}
            sx={{
                backgroundColor: colors.backgroundLight.secondary,
                border: `1px solid ${colors.buttons.primary.borderedBorder}`,
                color: colors.buttons.primary.color,
                px: 2.5,
                height: 32,
                mr: '16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                textTransform: 'uppercase',
                pointerEvents: 'auto',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.backgroundLight.primary,
                  borderColor: colors.buttons.primary.color,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(16, 55, 113, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 1px 2px rgba(16, 55, 113, 0.1)',
                }
              }}
          >
            UPDATE SOP
          </Box>
        )}
        {onManageSop && isDataOpsAdmin && (
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              mr: '16px',
              pointerEvents: 'auto',
            }}
          >
            <Box
              component="button"
              onClick={() => navigate('/dataops/manage-sop?type=OPS_PULSE')}
              sx={{
                backgroundColor: colors.backgroundLight.secondary,
                border: `1px solid ${colors.buttons.primary.borderedBorder}`,
                color: colors.buttons.primary.color,
                px: 2.5,
                height: 32,
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                textTransform: 'uppercase',
                pointerEvents: 'auto',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.backgroundLight.primary,
                  borderColor: colors.buttons.primary.color,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(16, 55, 113, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 1px 2px rgba(16, 55, 113, 0.1)',
                }
              }}
            >
              MANAGE SOP
            </Box>
            {userPendingCount > 0 && (
              <Typography
                onClick={() => navigate('/dataops/manage-sop?type=OPS_PULSE')}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  pt: '2px',
                  color: '#DC2626',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                {userPendingCount} {userPendingCount === 1 ? 'approval' : 'approvals'} pending
              </Typography>
            )}
          </Box>
        )}
      </CustomizedDataGrid>
      {total === 0 && (
        <Box sx={{ mt: 1, px: 1, py: 1, borderRadius: '4px', backgroundColor: `${theme.palette.table.pagination.BgColor} !important` }}>
          <Typography variant="bodyText" color={`${theme.palette.table.pagination.text} !important`}>
            Showing 0 - 0 of 0 results
          </Typography>
        </Box>
      )}

      <DetailedAlerts
        open={isAlertsDrawerOpen}
        onClose={() => setIsAlertsDrawerOpen(false)}
        onOpen={() => setIsAlertsDrawerOpen(true)}
        entireRow={entireRow}
        header={parentTile}
      />

        <Drawer
            anchor={'bottom'}
            open={openBottomDrawer.bottom}
            onClose={toggleDrawer(false, undefined, null, '', '')}
            PaperProps={{
              sx: {
                height: 'fit-content',
                background: `${colors.modal.background} !important`,
                color: "#e5e5e5"
              }
            }}
          >
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={toggleDrawer(false, undefined, null, "", "")}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 8,
                  color: "text.primary",
                  zIndex: 1,
                }}
              >
                <CloseIcon sx={{color: colors.modal.sectionTitle}} />
              </IconButton>

              <TasksRecords
                parent={openBottomDrawer.parent}
                parentTile={parentTile}
                state={openBottomDrawer.state}
                age={openBottomDrawer.age}
                sysId={openBottomDrawer.sysId}
              />
            </Box>
        </Drawer>
    </Box>
  );
});

export default IncidentsTable;